import { useState } from 'react';
import { WeddingInvitation } from '../types/invitation';
import { invitationService } from '../lib/supabase';
import { createLogger } from '../lib/logger';
import { BasicInfo, Customization } from './wizard/types';
import { invitationTemplates } from './wizard/data';
import { WizardStepNavigation, WizardNavigationButtons } from './wizard/components/WizardStepNavigation';
import { Step1_WeddingBasics } from './wizard/components/Step1_WeddingBasics';
import { Step2_TemplateSelection } from './wizard/components/Step2_TemplateSelection';
import { Step3_Customization } from './wizard/components/Step3_Customization';
import { Step4_Confirmation } from './wizard/components/Step4_Confirmation';
import { WizardSuccessScreen } from './wizard/components/WizardSuccessScreen';

const log = createLogger('app');

interface QuickInviteWizardProps {
  onComplete: (invitation: WeddingInvitation) => void;
  onBack: () => void;
}

export const QuickInviteWizard: React.FC<QuickInviteWizardProps> = ({ onComplete, onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [basicInfo, setBasicInfo] = useState<BasicInfo>({
    brideName: '',
    groomName: '',
    date: '',
    time: '14:00',
    venue: {
      name: '',
      address: '',
      mapUrl: ''
    },
    hasWelcomeParty: false,
    welcomePartyTime: '12:00',
    welcomePartyVenue: '',
    welcomePartyAddress: '',
    hasWeddingCeremony: true,
    ceremonyTime: '14:00',
    ceremonyVenue: '',
    ceremonyAddress: '',
    hasReception: true,
    receptionTime: '18:00',
    receptionVenue: '',
    receptionAddress: '',
    showTransportInfo: true,
    transportDetails: '',
    showContactInfo: true,
    contactPhone: '',
    contactEmail: '',
    coverImage: ''
  });
  const [customization, setCustomization] = useState<Customization>({
    primaryColor: '#ec4899',
    coverImage: '',
    welcomeMessage: ''
  });
  const [generatedCode, setGeneratedCode] = useState('');
  const [createdInvitation, setCreatedInvitation] = useState<WeddingInvitation | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const totalSteps = 4;

  const updateBasicInfo = (field: string, value: any) => {
    setBasicInfo(prev => {
      if (field.startsWith('venue.')) {
        const subField = field.split('.')[1];
        return {
          ...prev,
          venue: {
            ...prev.venue,
            [subField]: value
          }
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const generateInviteCode = () => {
    return Math.random().toString(36).substring(2, 8) + Date.now().toString(36);
  };

  const handleNext = async () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      setIsGenerating(true);

      try {
        const code = generateInviteCode();

        const selectedTemplateData = invitationTemplates.find(t => t.id === selectedTemplate);

        if (selectedTemplateData) {
          const invitationData: Omit<WeddingInvitation, 'id' | 'createdAt'> = {
            inviteCode: code,
            brideName: basicInfo.brideName,
            groomName: basicInfo.groomName,
            weddingDate: basicInfo.date,
            weddingTime: basicInfo.time,
            venue: basicInfo.venue.name,
            address: basicInfo.venue.address,
            hasWelcomeParty: basicInfo.hasWelcomeParty,
            hasWeddingCeremony: basicInfo.hasWeddingCeremony,
            hasReception: basicInfo.hasReception,
            showTransportInfo: basicInfo.showTransportInfo,
            showContactInfo: basicInfo.showContactInfo,
            transportDetails: basicInfo.transportDetails,
            welcomePartyVenue: basicInfo.welcomePartyVenue,
            welcomePartyAddress: basicInfo.welcomePartyAddress,
            welcomePartyTime: basicInfo.welcomePartyTime,
            ceremonyVenue: basicInfo.ceremonyVenue,
            ceremonyAddress: basicInfo.ceremonyAddress,
            ceremonyTime: basicInfo.ceremonyTime,
            receptionVenue: basicInfo.receptionVenue,
            receptionAddress: basicInfo.receptionAddress,
            receptionTime: basicInfo.receptionTime,
            mapUrl: basicInfo.venue.mapUrl,
            template: selectedTemplateData.name,
            coverImage: customization.coverImage,
            welcomeMessage: customization.welcomeMessage,
            primaryColor: customization.primaryColor,
            contactPhone: basicInfo.contactPhone,
            contactEmail: basicInfo.contactEmail,
            isActive: true,
            acceptSubmissions: true,
          };

          const generatedId = await invitationService.createInvitation(invitationData);

          const newInvitation: WeddingInvitation = {
            ...invitationData,
            id: generatedId,
            createdAt: new Date(),
          };

          setCreatedInvitation(newInvitation);
          setGeneratedCode(code);
        } else {
          log.error('未選擇邀請函範本，無法生成邀請函。');
          setIsGenerating(false);
          return;
        }
      } catch (error) {
        log.error('建立邀請函失敗:', error);

        let errorMessage = '建立邀請函失敗，請稍後再試';
        if (error && typeof error === 'object' && 'message' in error) {
          const message = String((error as any).message);
          if (message.includes('row-level security policy')) {
            errorMessage = 'RLS 政策錯誤：請在 Supabase 執行 database/20250622_force-disable-rls.sql';
          } else if (message.includes('401')) {
            errorMessage = '權限錯誤：請參考 documentation/20250622_development-rls-disable-guide.md 解決';
          } else {
            errorMessage = `建立失敗: ${message}`;
          }
        }

        alert(errorMessage);
        setIsGenerating(false);
        return;
      }

      setIsGenerating(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack();
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return basicInfo.brideName && basicInfo.groomName && basicInfo.date && basicInfo.venue.name;
      case 2:
        return selectedTemplate !== null;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  };

  if (generatedCode && createdInvitation) {
    return (
      <WizardSuccessScreen
        basicInfo={basicInfo}
        generatedCode={generatedCode}
        createdInvitation={createdInvitation}
        onComplete={onComplete}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <WizardStepNavigation
        currentStep={currentStep}
        totalSteps={totalSteps}
      />

      <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
        {currentStep === 1 && (
          <Step1_WeddingBasics basicInfo={basicInfo} updateBasicInfo={updateBasicInfo} />
        )}

        {currentStep === 2 && (
          <Step2_TemplateSelection
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            setCustomization={setCustomization}
          />
        )}

        {currentStep === 3 && (
          <Step3_Customization
            basicInfo={basicInfo}
            customization={customization}
            selectedTemplate={selectedTemplate}
            updateBasicInfo={updateBasicInfo}
            setCustomization={setCustomization}
          />
        )}

        {currentStep === 4 && (
          <Step4_Confirmation
            basicInfo={basicInfo}
            customization={customization}
            selectedTemplate={selectedTemplate}
            isGenerating={isGenerating}
          />
        )}
      </div>

      <WizardNavigationButtons
        currentStep={currentStep}
        totalSteps={totalSteps}
        canProceed={!!canProceed()}
        isGenerating={isGenerating}
        onBack={handleBack}
        onNext={handleNext}
        isFirstStep={currentStep === 1}
      />
    </div>
  );
};
